import { AudioMixer, type AudioBlock, type MeterLevels } from './audio';
import { Compositor, type OverlayConfig } from './compositor';
import { evenDim, negotiateAudioFormat, negotiateVideoFormat, type Quality } from './codecs';
import { openDisplay } from './devices';
import { indexRecording, type IndexProgress, type Thumbnail } from './indexer';
import { muxToBuffer } from './mux';
import { ClipPlayer } from './player';
import { createStore, type Store } from './store';
import type { ChunkLike } from './timeline';

export type RecorderPhase =
  | 'idle'
  | 'arming'
  | 'recording'
  | 'paused'
  | 'indexing'
  | 'ready'
  | 'failed';

export interface EncodedTrack<TChunk, TMeta> {
  chunks: TChunk[];
  /** Normalized timeline: entry i describes `chunks[i]`. */
  timeline: ChunkLike[];
  meta: TMeta | null;
}

export interface AudioTrack extends EncodedTrack<EncodedAudioChunk, EncodedAudioChunkMetadata> {
  codec: 'aac' | 'opus';
  channels: number;
  sampleRate: number;
}

export interface Recording {
  id: string;
  createdAt: number;
  label: string;
  width: number;
  height: number;
  fps: number;
  frameUs: number;
  durationUs: number;
  videoCodec: string;
  videoBitrate: number;
  video: EncodedTrack<EncodedVideoChunk, EncodedVideoChunkMetadata>;
  audio: AudioTrack | null;
  /** Indices into `video.chunks` that are random access points. */
  keyframes: number[];
  bytes: number;
  thumbs: Thumbnail[];
  peaks: Float32Array | null;
}

export interface LiveStats {
  frames: number;
  dropped: number;
  bytes: number;
  bitrate: number;
}

export interface EngineState {
  phase: RecorderPhase;
  error: string | null;
  notice: string | null;
  /** Milliseconds remaining on the pre-roll countdown, or null. */
  countdown: number | null;
  elapsedUs: number;
  recording: Recording | null;
  previewUrl: string | null;
  /** Playback wrapper for the trimmed preview; null until a take is indexed. */
  previewPlayer: ClipPlayer | null;
  stats: LiveStats | null;
  indexProgress: IndexProgress | null;
  displayLabel: string | null;
  capture: { width: number; height: number; fps: number } | null;
  audioActive: boolean;
}

export interface StartOptions {
  fps: number;
  quality: Quality;
  micStream: MediaStream | null;
  webcamStream: MediaStream | null;
  micGain: number;
  systemGain: number;
  wantSystemAudio: boolean;
  overlay: OverlayConfig | null;
  countdownMs: number;
  hideOwnTab: boolean;
  bitrateOverride?: number;
}

interface TrackProcessorLike {
  readable: ReadableStream<VideoFrame>;
}

/** Everything one capture session produced, captured before any teardown await. */
interface SessionData {
  videoChunks: EncodedVideoChunk[];
  videoTimeline: ChunkLike[];
  videoMeta: EncodedVideoChunkMetadata | null;
  audioChunks: EncodedAudioChunk[];
  audioTimeline: ChunkLike[];
  audioMeta: EncodedAudioChunkMetadata | null;
  keyframes: number[];
  width: number;
  height: number;
  frameUs: number;
  videoCodec: string;
  videoBitrate: number;
  audioCodec: 'aac' | 'opus' | null;
  audioChannels: number;
  audioSampleRate: number;
  bytes: number;
  label: string;
}

type TrackProcessorCtor = new (init: { track: MediaStreamTrack }) => TrackProcessorLike;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Two seconds keeps scrubbing responsive without inflating the file. */
const KEYFRAME_INTERVAL_SECONDS = 2;
/** Past this size an in-memory preview costs more than it gives. */
const PREVIEW_LIMIT_BYTES = 900 * 1024 * 1024;

/**
 * Owns a single capture session: display stream, encoders, audio graph, and the
 * normalized timeline that the editor and the exporter both read from.
 *
 * The engine lives entirely outside React. Components observe it through
 * `state` (a store shaped for `useSyncExternalStore`) and drive it with methods.
 */
export class ScreenRecorder {
  readonly state: Store<EngineState>;
  readonly meters: Store<MeterLevels>;

  private displayStream: MediaStream | null = null;
  private webcamElement: HTMLVideoElement | null = null;
  private compositor: Compositor | null = null;
  private mixer: AudioMixer | null = null;
  private meterUnsubscribe: (() => void) | null = null;
  private videoEncoder: VideoEncoder | null = null;
  private audioEncoder: AudioEncoder | null = null;
  private monitorElement: HTMLVideoElement | HTMLCanvasElement | null = null;
  private pumpElement: HTMLVideoElement | null = null;
  private pumpFrameHandle: number | null = null;
  private pumpInterval: ReturnType<typeof setInterval> | null = null;

  private videoChunks: EncodedVideoChunk[] = [];
  private videoTimeline: ChunkLike[] = [];
  private videoMeta: EncodedVideoChunkMetadata | null = null;
  private audioChunks: EncodedAudioChunk[] = [];
  private audioTimeline: ChunkLike[] = [];
  private audioMeta: EncodedAudioChunkMetadata | null = null;
  private keyframes: number[] = [];

  private captureSize = { width: 1920, height: 1080 };
  private videoBitrate = 0;
  private videoCodec = '';
  private audioCodec: 'aac' | 'opus' | null = null;
  private audioChannels = 2;
  private audioSampleRate = 48_000;

  private videoBytes = 0;
  private audioBytes = 0;
  private droppedFrames = 0;

  private t0 = 0;
  private pausedUs = 0;
  private pauseStartedAt = 0;
  private nextSlot = 1;
  private lastVideoTs = 0;
  private audioOriginUs: number | null = null;
  private lastAudioTs = 0;
  private frameUs = 33_333;
  private nextKeyframeSlot = 0;
  private keyframeEvery = 60;

  private stopping = false;
  private stopPromise: Promise<void> | null = null;
  private failureMessage: string | null = null;
  /**
   * Bumped whenever a session is reset. Async work captures the id it started
   * under and refuses to touch state once it has moved on — otherwise a slow
   * teardown or indexer could resurrect a discarded session's phase and leave
   * the recorder apparently dead.
   */
  private sessionId = 0;
  private videoReader: ReadableStreamDefaultReader<VideoFrame> | null = null;
  private pumpPromise: Promise<void> | null = null;
  private statsTimer: ReturnType<typeof setInterval> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.state = createStore<EngineState>({
      phase: 'idle',
      error: null,
      notice: null,
      countdown: null,
      elapsedUs: 0,
      recording: null,
      previewUrl: null,
      previewPlayer: null,
      stats: null,
      indexProgress: null,
      displayLabel: null,
      capture: null,
      audioActive: false,
    });
    this.meters = createStore<MeterLevels>({ mic: 0, system: 0, mix: 0 });
  }

  get monitor(): HTMLVideoElement | HTMLCanvasElement | null {
    return this.monitorElement;
  }

  /**
   * Mounts the live preview surface into `container`: the compositor canvas
   * when an inset is active, otherwise a video element on the capture stream.
   * Returns the matching cleanup.
   */
  attachMonitor(container: HTMLElement): () => void {
    const element = this.monitorElement;
    if (!element) return () => undefined;
    container.appendChild(element);
    return () => {
      if (element.parentElement === container) container.removeChild(element);
    };
  }

  async start(options: StartOptions): Promise<void> {
    const { phase } = this.state.getSnapshot();
    if (phase !== 'idle' && phase !== 'failed') return;

    this.resetSession();
    const session = this.sessionId;
    this.state.setState({
      phase: 'arming',
      error: null,
      notice: null,
      countdown: options.countdownMs > 0 ? options.countdownMs : null,
      recording: null,
      previewUrl: null,
      previewPlayer: null,
      stats: null,
      indexProgress: null,
    });

    try {
      const displayStream = await openDisplay({
        fps: options.fps,
        wantSystemAudio: options.wantSystemAudio,
        hideOwnTab: options.hideOwnTab,
      });
      if (session !== this.sessionId) {
        for (const track of displayStream.getTracks()) track.stop();
        return;
      }
      this.displayStream = displayStream;

      const track = displayStream.getVideoTracks()[0];
      if (!track) throw new Error('The shared source did not include a video track.');
      const settings = track.getSettings();
      const width = evenDim(settings.width ?? 1920);
      const height = evenDim(settings.height ?? 1080);
      this.captureSize = { width, height };
      this.frameUs = Math.round(1_000_000 / options.fps);

      // Build the monitor before the countdown so the operator can see exactly
      // what is about to be captured.
      await this.prepareSurface(displayStream, options);
      if (session !== this.sessionId) return;
      this.state.setState({
        displayLabel: track.label || 'Display',
        capture: { width, height, fps: options.fps },
      });

      if (options.countdownMs > 0) await this.runCountdown(options.countdownMs);
      // The countdown is cancellable: a reset during it must win.
      if (session !== this.sessionId) return;
      if (this.stopping || this.failureMessage) return;

      await this.beginCapture(displayStream, options, session);
    } catch (error) {
      if (session === this.sessionId) this.fail(error);
    }
  }

  private async prepareSurface(displayStream: MediaStream, options: StartOptions): Promise<void> {
    const { width, height } = this.captureSize;
    if (options.overlay && options.webcamStream) {
      const element = document.createElement('video');
      element.srcObject = options.webcamStream;
      element.muted = true;
      element.playsInline = true;
      await element.play().catch(() => undefined);
      this.webcamElement = element;
      this.compositor = new Compositor(width, height);
      this.compositor.setWebcam(element);
      this.monitorElement = this.compositor.canvas;
      return;
    }
    const monitor = document.createElement('video');
    monitor.srcObject = displayStream;
    monitor.muted = true;
    monitor.playsInline = true;
    monitor.className = 'monitor-surface';
    void monitor.play().catch(() => undefined);
    this.monitorElement = monitor;
  }

  private runCountdown(totalMs: number): Promise<void> {
    return new Promise((resolve) => {
      let remaining = totalMs;
      this.countdownTimer = setInterval(() => {
        remaining -= 100;
        if (remaining <= 0) {
          if (this.countdownTimer) clearInterval(this.countdownTimer);
          this.countdownTimer = null;
          this.state.setState({ countdown: null });
          resolve();
          return;
        }
        this.state.setState({ countdown: remaining });
      }, 100);
    });
  }

  private async beginCapture(
    displayStream: MediaStream,
    options: StartOptions,
    session: number,
  ): Promise<void> {
    const videoTrack = displayStream.getVideoTracks()[0];
    if (!videoTrack) throw new Error('The shared source did not include a video track.');

    const settings = videoTrack.getSettings();
    const width = evenDim(settings.width ?? 1920);
    const height = evenDim(settings.height ?? 1080);
    const fps = options.fps;
    this.frameUs = Math.round(1_000_000 / fps);
    this.keyframeEvery = Math.max(2, Math.round(fps * KEYFRAME_INTERVAL_SECONDS));
    this.captureSize = { width, height };

    const videoFormat = await negotiateVideoFormat(
      width,
      height,
      fps,
      options.quality,
      options.bitrateOverride,
    );
    const audioFormat = await negotiateAudioFormat(48_000, 2, 160_000);

    const systemTracks =
      options.wantSystemAudio && displayStream.getAudioTracks().length > 0
        ? new MediaStream(displayStream.getAudioTracks())
        : null;

    let notice: string | null = null;
    if (options.wantSystemAudio && !systemTracks) {
      notice =
        'The picker returned no system audio. Tick "Share audio" when choosing a source, or record with the microphone instead.';
    } else if (!audioFormat && (options.micStream || systemTracks)) {
      notice = 'Audio encoding is unavailable in this browser, so this take is video only.';
    }

    this.videoCodec = videoFormat.codec;
    this.videoBitrate = videoFormat.bitrate;
    this.audioCodec = audioFormat?.containerCodec ?? null;
    this.audioChannels = audioFormat?.numberOfChannels ?? 2;
    this.audioSampleRate = audioFormat?.sampleRate ?? 48_000;

    this.videoEncoder = new VideoEncoder({
      output: (chunk, meta) => this.onVideoChunk(chunk, meta),
      error: (error) => this.fail(error),
    });
    this.videoEncoder.configure({
      codec: videoFormat.codec,
      width,
      height,
      bitrate: videoFormat.bitrate,
      framerate: fps,
      latencyMode: 'realtime',
      hardwareAcceleration: 'no-preference',
      avc: { format: 'avc' },
    });

    if (audioFormat) {
      this.audioEncoder = new AudioEncoder({
        output: (chunk, meta) => this.onAudioChunk(chunk, meta),
        error: (error) => this.fail(error),
      });
      this.audioEncoder.configure({
        codec: audioFormat.codec,
        sampleRate: audioFormat.sampleRate,
        numberOfChannels: audioFormat.numberOfChannels,
        bitrate: audioFormat.bitrate,
      });
    }

    if (audioFormat) {
      try {
        this.mixer = await AudioMixer.create({
          micStream: options.micStream,
          systemStream: systemTracks,
          micGain: options.micGain,
          systemGain: options.systemGain,
          sampleRate: audioFormat.sampleRate,
          onBlock: (block) => this.onAudioBlock(block),
          onError: (error) => this.fail(error),
        });
        if (this.mixer) {
          this.meterUnsubscribe = this.mixer.meters.subscribe(() => {
            const snapshot = this.mixer?.meters.getSnapshot();
            if (snapshot) this.meters.setState(snapshot);
          });
        }
      } catch (error) {
        notice = `Audio input unavailable: ${describeError(error)}`;
        this.audioCodec = null;
      }
    }

    if (session !== this.sessionId) return;
    if (this.stopping || this.failureMessage) {
      await this.stop();
      return;
    }

    const useProcessor =
      !this.compositor &&
      typeof (globalThis as { MediaStreamTrackProcessor?: TrackProcessorCtor })
        .MediaStreamTrackProcessor === 'function';

    this.t0 = performance.now();
    this.state.setState({
      phase: 'recording',
      notice,
      countdown: null,
      elapsedUs: 0,
      stats: { frames: 0, dropped: 0, bytes: 0, bitrate: 0 },
      displayLabel: videoTrack.label || 'Display',
      capture: { width, height, fps },
      audioActive: Boolean(this.mixer),
    });

    this.pumpPromise = useProcessor
      ? this.pumpWithTrackProcessor(videoTrack, session)
      : this.pumpWithCanvas(videoTrack, options.overlay, session);
    this.startStatsTimer();

    videoTrack.addEventListener('ended', () => {
      if (session === this.sessionId) void this.stop();
    });
  }

  /**
   * Chunk timestamps come from the encoder, which mirrors the CFR grid we feed
   * it. Anything non-monotonic falls back to the frame grid so the muxer never
   * sees a bad timestamp.
   */
  private onVideoChunk(chunk: EncodedVideoChunk, meta?: EncodedVideoChunkMetadata): void {
    if (!this.videoMeta && meta?.decoderConfig?.description) this.videoMeta = meta;
    const index = this.videoChunks.length;
    this.videoChunks.push(chunk);
    this.videoBytes += chunk.byteLength;
    const timestamp = normalizeTimestamp(chunk.timestamp, this.lastVideoTs, this.frameUs);
    this.lastVideoTs = timestamp;
    this.videoTimeline.push({ timestamp, duration: this.frameUs, type: chunk.type });
    if (chunk.type === 'key') this.keyframes.push(index);
  }

  private onAudioChunk(chunk: EncodedAudioChunk, meta?: EncodedAudioChunkMetadata): void {
    if (!this.audioMeta && meta?.decoderConfig) this.audioMeta = meta;
    const frameDuration = (1024 / this.audioSampleRate) * 1_000_000;
    const timestamp = normalizeTimestamp(chunk.timestamp, this.lastAudioTs - frameDuration, frameDuration);
    this.audioChunks.push(chunk);
    this.audioBytes += chunk.byteLength;
    this.audioTimeline.push({
      timestamp,
      duration: chunk.duration && chunk.duration > 0 ? chunk.duration : frameDuration,
      type: 'key',
    });
    this.lastAudioTs = timestamp + frameDuration;
  }

  private onAudioBlock(block: AudioBlock): void {
    const { phase } = this.state.getSnapshot();
    if (phase !== 'recording') return;
    const encoder = this.audioEncoder;
    if (!encoder || encoder.state !== 'configured') return;

    if (this.audioOriginUs === null) {
      // Anchor the audio clock to the wall clock the instant the first block
      // lands, minus the graph's output latency, so both tracks share an origin.
      const latencyUs = (this.mixer?.context.baseLatency ?? 0) * 1_000_000;
      const nowUs = (performance.now() - this.t0) * 1000 - this.pausedUs - latencyUs;
      this.audioOriginUs = Math.max(0, nowUs);
      this.lastAudioTs = this.audioOriginUs;
    }

    const timestamp = this.lastAudioTs;
    this.lastAudioTs = timestamp + (block.length / block.sampleRate) * 1_000_000;

    try {
      const data = new AudioData({
        format: 'f32-planar',
        sampleRate: block.sampleRate,
        numberOfFrames: block.length,
        numberOfChannels: block.channels,
        timestamp,
        data: block.data,
      });
      encoder.encode(data);
      data.close();
    } catch (error) {
      this.fail(error);
    }
  }

  private async consumeFrame(source: CanvasImageSource): Promise<void> {
    const encoder = this.videoEncoder;
    if (!encoder || encoder.state !== 'configured') return;

    const nowUs = (performance.now() - this.t0) * 1000 - this.pausedUs;
    const slot = Math.floor(nowUs / this.frameUs);
    if (slot < this.nextSlot) {
      // A frame landed inside a slot we already encoded: the display is running
      // faster than the target frame rate.
      this.droppedFrames += 1;
      return;
    }
    const slotIndex = this.nextSlot;
    this.nextSlot = slot + 1;
    const slotTs = slotIndex * this.frameUs;

    // Backpressure: 4K capture can easily outrun the encoder.
    let guard = 0;
    while (encoder.encodeQueueSize > 6 && guard < 150 && !this.stopping) {
      await delay(4);
      guard += 1;
    }
    if (this.stopping) return;

    const keyFrame = slotIndex >= this.nextKeyframeSlot;
    if (keyFrame) this.nextKeyframeSlot = slotIndex + this.keyframeEvery;

    try {
      const frame = new VideoFrame(source, { timestamp: slotTs, duration: this.frameUs });
      encoder.encode(frame, { keyFrame });
      frame.close();
    } catch (error) {
      this.fail(error);
    }
  }

  private async pumpWithTrackProcessor(track: MediaStreamTrack, session: number): Promise<void> {
    const ctor = (globalThis as { MediaStreamTrackProcessor?: TrackProcessorCtor })
      .MediaStreamTrackProcessor;
    if (!ctor) return;
    const processor = new ctor({ track });
    const reader = processor.readable.getReader();
    this.videoReader = reader;
    try {
      while (!this.stopping && session === this.sessionId) {
        const { done, value } = await reader.read();
        if (done) break;
        try {
          await this.consumeFrame(value);
        } finally {
          value.close();
        }
      }
    } catch (error) {
      if (!this.stopping) this.fail(error);
    } finally {
      try {
        reader.releaseLock();
      } catch {
        // already released
      }
    }
  }

  private async pumpWithCanvas(
    track: MediaStreamTrack,
    overlay: OverlayConfig | null,
    session: number,
  ): Promise<void> {
    const compositor = this.compositor;
    const video = document.createElement('video');
    video.srcObject = new MediaStream([track]);
    video.muted = true;
    video.playsInline = true;
    this.pumpElement = video;
    await video.play().catch(() => undefined);

    let busy = false;
    const capture = async () => {
      if (this.stopping || busy || session !== this.sessionId) return;
      busy = true;
      try {
        if (compositor) {
          compositor.draw(video, overlay);
          await this.consumeFrame(compositor.canvas);
        } else {
          await this.consumeFrame(video);
        }
      } catch (error) {
        this.fail(error);
      } finally {
        busy = false;
      }
    };

    if (typeof video.requestVideoFrameCallback === 'function') {
      const onFrame = () => {
        if (this.stopping || session !== this.sessionId) return;
        this.pumpFrameHandle = video.requestVideoFrameCallback(onFrame);
        void capture();
      };
      this.pumpFrameHandle = video.requestVideoFrameCallback(onFrame);
    } else {
      this.pumpInterval = setInterval(
        () => void capture(),
        Math.max(8, Math.round(this.frameUs / 1000)),
      );
    }

    while (!this.stopping && session === this.sessionId) await delay(80);
  }

  private startStatsTimer(): void {
    if (this.statsTimer !== null) clearInterval(this.statsTimer);
    this.statsTimer = setInterval(() => {
      const { phase } = this.state.getSnapshot();
      if (phase !== 'recording' && phase !== 'paused') return;
      const bytes = this.videoBytes + this.audioBytes;
      const elapsedUs = this.elapsedUs();
      this.state.setState({
        elapsedUs,
        stats: {
          frames: this.videoChunks.length,
          dropped: this.droppedFrames,
          bytes,
          bitrate: elapsedUs > 0 ? (bytes * 8 * 1_000_000) / elapsedUs : 0,
        },
      });
    }, 250);
  }

  private elapsedUs(): number {
    if (this.t0 === 0) return 0;
    return Math.max(0, (performance.now() - this.t0) * 1000 - this.pausedUs);
  }

  pause(): void {
    if (this.state.getSnapshot().phase !== 'recording') return;
    this.pauseStartedAt = performance.now();
    this.state.setState({ phase: 'paused' });
  }

  resume(): void {
    if (this.state.getSnapshot().phase !== 'paused') return;
    this.pausedUs += (performance.now() - this.pauseStartedAt) * 1000;
    this.state.setState({ phase: 'recording' });
  }

  setGains(mic: number, system: number): void {
    this.mixer?.setGains(mic, system);
  }

  stop(): Promise<void> {
    if (this.stopPromise) return this.stopPromise;
    this.stopping = true;
    this.stopPromise = this.finish();
    return this.stopPromise;
  }

  private async finish(): Promise<void> {
    const session = this.sessionId;

    // Detach this session's resources synchronously, before any await, so that
    // a new session can start while this one is still tearing down without the
    // two sharing a timer, an encoder, or the answer to "are we stopping?".
    const countdownTimer = this.countdownTimer;
    const statsTimer = this.statsTimer;
    const pumpInterval = this.pumpInterval;
    const pumpElement = this.pumpElement;
    const pumpFrameHandle = this.pumpFrameHandle;
    const reader = this.videoReader;
    const pumpPromise = this.pumpPromise;
    const displayStream = this.displayStream;
    const meterUnsubscribe = this.meterUnsubscribe;
    const mixer = this.mixer;
    const videoEncoder = this.videoEncoder;
    const audioEncoder = this.audioEncoder;
    const webcamElement = this.webcamElement;
    const failure = this.failureMessage;

    this.countdownTimer = null;
    this.statsTimer = null;
    this.pumpInterval = null;
    this.pumpElement = null;
    this.pumpFrameHandle = null;
    this.videoReader = null;
    this.pumpPromise = null;
    this.displayStream = null;
    this.meterUnsubscribe = null;
    this.mixer = null;
    this.videoEncoder = null;
    this.audioEncoder = null;
    this.webcamElement = null;

    const data: SessionData = {
      videoChunks: this.videoChunks,
      videoTimeline: this.videoTimeline,
      videoMeta: this.videoMeta,
      audioChunks: this.audioChunks,
      audioTimeline: this.audioTimeline,
      audioMeta: this.audioMeta,
      keyframes: this.keyframes,
      width: this.captureSize.width,
      height: this.captureSize.height,
      frameUs: this.frameUs,
      videoCodec: this.videoCodec,
      videoBitrate: this.videoBitrate,
      audioCodec: this.audioCodec,
      audioChannels: this.audioChannels,
      audioSampleRate: this.audioSampleRate,
      bytes: this.videoBytes + this.audioBytes,
      label: this.state.getSnapshot().displayLabel ?? 'Display',
    };

    if (countdownTimer) clearInterval(countdownTimer);
    if (statsTimer) clearInterval(statsTimer);
    if (pumpInterval) clearInterval(pumpInterval);
    if (pumpFrameHandle !== null && pumpElement) {
      pumpElement.cancelVideoFrameCallback?.(pumpFrameHandle);
    }

    try {
      await reader?.cancel();
    } catch {
      // reader already finished
    }
    try {
      await pumpPromise;
    } catch {
      // pump failures surface through the encoder error callback
    }

    for (const track of displayStream?.getTracks() ?? []) track.stop();

    meterUnsubscribe?.();
    try {
      await mixer?.close();
    } catch {
      // graph already torn down
    }
    this.meters.setState({ mic: 0, system: 0, mix: 0 });
    webcamElement?.pause();
    if (pumpElement) pumpElement.srcObject = null;

    await flushEncoder(videoEncoder);
    await flushEncoder(audioEncoder);
    videoEncoder?.close();
    audioEncoder?.close();

    // From here on the take belongs to this session; a reset means it is gone.
    if (session !== this.sessionId) return;

    if (failure) {
      this.state.setState({
        phase: 'failed',
        error: failure,
        countdown: null,
        stats: null,
        capture: null,
        displayLabel: null,
      });
      return;
    }

    if (data.videoChunks.length === 0) {
      this.resetSession();
      this.state.setState({
        phase: 'idle',
        previewUrl: null,
        stats: null,
        capture: null,
        displayLabel: null,
        countdown: null,
        notice:
          'No frames arrived from that source, so there is nothing to edit. On macOS the browser needs "Screen & System Audio Recording" permission in System Settings → Privacy & Security, and it must be restarted after you grant it.',
      });
      return;
    }

    finalizeTimeline(data.videoTimeline);
    finalizeTimeline(data.audioTimeline);

    const recording = this.buildRecording(data);
    this.state.setState({
      phase: 'indexing',
      recording,
      previewUrl: null,
      stats: null,
      elapsedUs: recording.durationUs,
      indexProgress: { done: 0, total: 0, stage: 'filmstrip' },
      capture: { width: recording.width, height: recording.height, fps: recording.fps },
    });

    const originUs = recording.video.timeline[0].timestamp;
    if (recording.bytes <= PREVIEW_LIMIT_BYTES) {
      try {
        const buffer = await muxToBuffer({
          container: 'mp4',
          width: recording.width,
          height: recording.height,
          fps: recording.fps,
          video: {
            chunks: recording.video.chunks,
            timeline: recording.video.timeline,
            meta: recording.video.meta,
            startIndex: 0,
            endIndex: recording.video.chunks.length,
            originUs,
          },
          audio: recording.audio
            ? {
                chunks: recording.audio.chunks,
                timeline: recording.audio.timeline,
                meta: recording.audio.meta,
                startIndex: 0,
                endIndex: recording.audio.chunks.length,
                originUs,
                codec: recording.audio.codec,
                channels: recording.audio.channels,
                sampleRate: recording.audio.sampleRate,
              }
            : null,
        });
        if (session !== this.sessionId) return;
        const url = URL.createObjectURL(new Blob([buffer], { type: 'video/mp4' }));
        const player = new ClipPlayer(url);
        if (session !== this.sessionId) {
          URL.revokeObjectURL(url);
          player.dispose();
          return;
        }
        this.state.setState({ previewUrl: url, previewPlayer: player });
      } catch (error) {
        if (session === this.sessionId) {
          this.state.setState({ notice: `Preview unavailable: ${describeError(error)}` });
        }
      }
    } else {
      this.state.setState({
        notice:
          'This take is large, so the in-tab preview was skipped. Exporting still works — keep the tab open until it finishes.',
      });
    }

    const take = this.state.getSnapshot().recording;
    if (take && session === this.sessionId) {
      try {
        const result = await indexRecording(take, (progress) => {
          if (session === this.sessionId) this.state.setState({ indexProgress: progress });
        });
        take.thumbs = result.thumbs;
        take.peaks = result.peaks;
      } catch {
        // The timeline falls back to a plain ruler.
      }
    }
    if (session === this.sessionId) {
      this.state.setState({ phase: 'ready', indexProgress: null });
    }
  }

  private buildRecording(data: SessionData): Recording {
    const last = data.videoTimeline[data.videoTimeline.length - 1];
    const durationUs = last ? last.timestamp + last.duration : 0;

    return {
      id: `take-${Date.now().toString(36)}`,
      createdAt: Date.now(),
      label: data.label,
      width: data.width,
      height: data.height,
      fps: Math.round(1_000_000 / data.frameUs),
      frameUs: data.frameUs,
      durationUs,
      videoCodec: data.videoCodec,
      videoBitrate: data.videoBitrate,
      video: { chunks: data.videoChunks, timeline: data.videoTimeline, meta: data.videoMeta },
      audio:
        data.audioCodec && data.audioChunks.length > 0
          ? {
              chunks: data.audioChunks,
              timeline: data.audioTimeline,
              meta: data.audioMeta,
              codec: data.audioCodec,
              channels: data.audioChannels,
              sampleRate: data.audioSampleRate,
            }
          : null,
      keyframes: data.keyframes,
      bytes: data.bytes,
      thumbs: [],
      peaks: null,
    };
  }

  private fail(error: unknown): void {
    if (this.failureMessage) return;
    this.failureMessage = describeError(error);
    this.stopping = true;
    void this.stop();
  }

  discard(): void {
    const { previewUrl: url, previewPlayer } = this.state.getSnapshot();
    if (url) URL.revokeObjectURL(url);
    previewPlayer?.dispose();
    // Invalidate anything still running for the outgoing session.
    this.stopping = true;
    for (const track of this.displayStream?.getTracks() ?? []) track.stop();
    const mixer = this.mixer;
    this.mixer = null;
    void mixer?.close().catch(() => undefined);
    this.meterUnsubscribe?.();
    this.meterUnsubscribe = null;
    this.resetSession();
    this.state.setState({
      phase: 'idle',
      error: null,
      notice: null,
      recording: null,
      previewUrl: null,
      previewPlayer: null,
      stats: null,
      countdown: null,
      elapsedUs: 0,
      capture: null,
      displayLabel: null,
      indexProgress: null,
      audioActive: false,
    });
  }

  private resetSession(): void {
    const { previewUrl, previewPlayer } = this.state.getSnapshot();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    previewPlayer?.dispose();
    this.sessionId += 1;
    this.videoChunks = [];
    this.videoMeta = null;
    this.videoTimeline = [];
    this.audioChunks = [];
    this.audioTimeline = [];
    this.audioMeta = null;
    this.keyframes = [];
    this.compositor = null;
    this.monitorElement = null;
    this.pumpElement = null;
    this.webcamElement = null;
    this.videoEncoder = null;
    this.audioEncoder = null;
    this.videoReader = null;
    this.pumpPromise = null;
    this.displayStream = null;
    this.droppedFrames = 0;
    this.videoBytes = 0;
    this.audioBytes = 0;
    this.lastVideoTs = 0;
    this.lastAudioTs = 0;
    this.audioOriginUs = null;
    this.nextSlot = 1;
    this.nextKeyframeSlot = 0;
    this.pausedUs = 0;
    this.t0 = 0;
    this.stopping = false;
    this.stopPromise = null;
    this.failureMessage = null;
  }
}

/** Keep encoder timestamps sane: finite, monotonic, and at least one step apart. */
function normalizeTimestamp(raw: number, previous: number, stepUs: number): number {
  if (!Number.isFinite(raw) || raw <= previous) return previous + stepUs;
  return raw;
}

/** Derive per-chunk durations from the gaps, which is what a player will use. */
function finalizeTimeline(timeline: ChunkLike[]): void {
  for (let i = 0; i < timeline.length - 1; i += 1) {
    timeline[i].duration = Math.max(1, timeline[i + 1].timestamp - timeline[i].timestamp);
  }
}

async function flushEncoder(encoder: VideoEncoder | AudioEncoder | null): Promise<void> {
  if (!encoder || encoder.state !== 'configured') return;
  try {
    await encoder.flush();
  } catch {
    // The encoder errored; whatever chunks exist are still usable.
  }
}

export function describeError(error: unknown): string {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError') return 'Permission was dismissed, so nothing was shared.';
    if (error.name === 'NotFoundError') return 'No matching capture source was found.';
    if (error.name === 'NotReadableError') {
      return 'The system refused to start the capture — it is either in use by another app or blocked. On macOS, grant "Screen & System Audio Recording" permission to your browser in System Settings → Privacy & Security, then restart it.';
    }
    if (error.name === 'AbortError') return 'The capture was cancelled.';
    return `${error.name}: ${error.message}`;
  }
  if (error instanceof Error) return error.message;
  return String(error);
}
